import { useState, useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Gift } from "lucide-react";
import { toast } from "sonner";
import { fmtErr } from "@/lib/api";

export default function Register() {
  const { register } = useAuth();
  const nav = useNavigate();
  const [searchParams] = useSearchParams();
  const [form, setForm] = useState({ name: "", email: "", password: "", role: "guest" });
  const [termsOk, setTermsOk] = useState(false);
  const [loading, setLoading] = useState(false);

  // Capture ?ref= from URL OR from a previous visit stored in localStorage
  const [refCode, setRefCode] = useState(() => {
    const q = searchParams.get("ref");
    if (q) {
      try { localStorage.setItem("gs_ref", q); } catch (_) { /* private mode */ }
      return q;
    }
    try { return localStorage.getItem("gs_ref") || ""; } catch (_) { return ""; }
  });
  // Handle deep-link case
  useEffect(() => {
    const q = searchParams.get("ref");
    if (q && q !== refCode) {
      setRefCode(q);
      try { localStorage.setItem("gs_ref", q); } catch (_) { /* private mode */ }
    }
  }, [searchParams, refCode]);

  const submit = async (e) => {
    e.preventDefault();
    if (!termsOk) {
      toast.error("Please accept the Terms of Service and Privacy Policy to continue.");
      return;
    }
    setLoading(true);
    try {
      const payload = { ...form, terms_accepted: true };
      if (refCode) payload.referred_by = refCode;
      const u = await register(payload);
      try { localStorage.removeItem("gs_ref"); } catch (_) { /* ignore */ }
      toast.success(`Welcome to Guest Smell, ${u.name}`);
      nav(u.role === "host" ? "/host" : "/browse");
    } catch (e) {
      toast.error(fmtErr(e));
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-[calc(100vh-4rem)] scent-wave flex items-center justify-center p-8">
      <form onSubmit={submit} data-testid="register-form" className="w-full max-w-lg bg-white rounded-3xl border border-slate-200 p-8 md:p-10">
        <div className="text-xs tracking-[0.28em] uppercase text-emerald-700 font-semibold">Create Account</div>
        <h2 className="mt-2 font-serif text-4xl text-[#0A1128] tracking-tight">Join Guest Smell™</h2>

        {refCode && (
          <div className="mt-5 p-4 rounded-2xl bg-emerald-50 border border-emerald-200 flex items-center gap-3" data-testid="referral-badge">
            <Gift className="w-5 h-5 text-emerald-600 shrink-0" />
            <div className="text-sm text-emerald-900 leading-snug">
              You were invited by a Guest Smell host. Sign up and, once you certify your first property, they&apos;ll earn $25 in credit.
            </div>
          </div>
        )}

        <div className="mt-8 space-y-4">
          <div>
            <Label>I am a…</Label>
            <RadioGroup value={form.role} onValueChange={(v)=>setForm({...form, role: v})} className="mt-2 grid grid-cols-2 gap-3">
              <label className={`cursor-pointer border rounded-2xl p-4 transition-colors ${form.role==="guest" ? "border-emerald-500 bg-emerald-50" : "border-slate-200"}`}>
                <RadioGroupItem value="guest" className="sr-only" data-testid="register-role-guest" />
                <div className="font-serif text-lg text-[#0A1128]">Guest</div>
                <div className="text-xs text-slate-500 mt-1">Browse certified stays</div>
              </label>
              <label className={`cursor-pointer border rounded-2xl p-4 transition-colors ${form.role==="host" ? "border-[#D4AF37] bg-[#F9F3E5]" : "border-slate-200"}`}>
                <RadioGroupItem value="host" className="sr-only" data-testid="register-role-host" />
                <div className="font-serif text-lg text-[#0A1128]">Host</div>
                <div className="text-xs text-slate-500 mt-1">Certify my properties</div>
              </label>
            </RadioGroup>
          </div>

          <div>
            <Label htmlFor="reg-name">Full name</Label>
            <Input id="reg-name" data-testid="register-name" value={form.name}
              onChange={(e)=>setForm({...form, name: e.target.value})} required className="mt-1" />
          </div>
          <div>
            <Label htmlFor="reg-email">Email</Label>
            <Input id="reg-email" type="email" data-testid="register-email" value={form.email}
              onChange={(e)=>setForm({...form, email: e.target.value})} required className="mt-1" />
          </div>
          <div>
            <Label htmlFor="reg-password">Password</Label>
            <Input id="reg-password" type="password" data-testid="register-password" value={form.password}
              onChange={(e)=>setForm({...form, password: e.target.value})} required minLength={6} className="mt-1" />
          </div>

          {/* Terms + Privacy acceptance */}
          <label className="flex items-start gap-3 pt-2 cursor-pointer">
            <Checkbox
              checked={termsOk}
              onCheckedChange={(v)=>setTermsOk(!!v)}
              data-testid="register-terms-checkbox"
              className="mt-1"
            />
            <span className="text-sm text-slate-600 leading-snug">
              I have read and agree to the{" "}
              <Link to="/terms" target="_blank" className="text-emerald-700 hover:underline">Terms of Service</Link>,{" "}
              <Link to="/privacy" target="_blank" className="text-emerald-700 hover:underline">Privacy Policy</Link>,
              and{" "}
              <Link to="/refund" target="_blank" className="text-emerald-700 hover:underline">Refund Policy</Link>.
            </span>
          </label>
        </div>

        <Button type="submit" disabled={loading || !termsOk} data-testid="register-submit"
          className="mt-8 w-full rounded-full bg-[#0A1128] hover:bg-[#1A2A3A] text-white h-11 disabled:opacity-50">
          {loading ? "Creating…" : "Create Account"}
        </Button>

        <p className="mt-6 text-sm text-slate-500">
          Already registered? <Link to="/login" className="text-emerald-700 font-medium">Sign in</Link>
        </p>
      </form>
    </div>
  );
}
